import { useEffect, useMemo, useState } from "react";
import { AGGridDefault, ModalTable, Pagination } from "../../../../components";
import { ColDef } from "ag-grid-community";
import { IQueryPredictiveOrderExecutions } from "../../../../interfaces/IQuery";
import { useTranslation } from "react-i18next";
import { TagCapsules } from "../../../../components";
import { IColor } from "../../../../utils/selectColors";
import dataProcessing from "../../../../utils/dataProcessing";
import {
  convertToDateAndHour,
  formatUtcDateOnly,
} from "../../../../utils/date";
import {
  ECategoryPredictive,
  useExecutionsPredictiveOrder,
} from "../../../../hooks/usePredictives";
import { formatCurrency } from "../../../../utils/currency";
import {
  colorTextPredictiveOrderPercentage,
  colorTextPredictiveOrderSide,
  colorTextPredictiveOrderStatus,
  orderTypeText,
  validityText,
} from "../../../../utils/PredictivesFunctions";

interface IProps {
  id: number | null;
  setClose: () => void;
}

export default function ModalDetails({ id, setClose }: IProps) {
  const { t } = useTranslation();
  const [queryDetail, setQueryDetail] =
    useState<IQueryPredictiveOrderExecutions>({
      id: !!id ? id : 0,
      page: 1,
      pageSize: 300,
    });

  useEffect(() => {
    if (!id) return;
    setQueryDetail((prev) => ({ ...prev, id: id }));
  }, [id]);

  const { data, isLoading, isFetching } =
    useExecutionsPredictiveOrder(queryDetail);

  const pagination = {
    page: queryDetail.page,
    pageSize: queryDetail.pageSize,
    totalElements: data?.totalElements,
    totalPages: data?.totalPages,
    view: data?.data?.length || 0,
  };

  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: t("operacoes.preditivos.table.category"),
        field: "category",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => {
          return params.data?.category ? (
            <TagCapsules
              mr={12}
              border
              colorTag={
                params.data?.category === ECategoryPredictive.copom
                  ? IColor.copom
                  : IColor.green
              }
            >
              {params.data?.category}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.preditivos.table.contract"),
        field: "percentage",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => {
          const percentageColorText = colorTextPredictiveOrderPercentage(
            params.data?.percentage
          );
          return (
            <TagCapsules border colorTag={percentageColorText.color}>
              {`${t(percentageColorText.text)} ${
                !!percentageColorText.percentage
                  ? ` ${percentageColorText.percentage}%`
                  : ""
              }`}
            </TagCapsules>
          );
        },
      },
      {
        headerName: t("operacoes.preditivos.table.response"),
        field: "order_side",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => {
          const orderSide = colorTextPredictiveOrderSide(
            params.data?.order_side
          );
          return params.data?.order_side ? (
            <TagCapsules border colorTag={orderSide.color}>
              {t(orderSide.text)}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.preditivos.table.expiration"),
        field: "expiration_product",
        suppressColumnsToolPanel: true,
        width: 200,
        cellRenderer: (params: any) => {
          return `${formatUtcDateOnly(params.data?.expiration_product)}`;
        },
      },
      {
        headerName: t("operacoes.preditivos.table.volume"),
        field: "quantity",
        suppressColumnsToolPanel: true,
        width: 90,
        cellRenderer: (params: any) => dataProcessing(params.data?.quantity),
      },
      {
        headerName: t("operacoes.preditivos.table.probability"),
        field: "probability",
        suppressColumnsToolPanel: true,
        width: 80,
        cellRenderer: (params: any) =>
          params.data?.probability ? `${params.data?.probability}%` : "---",
      },
      {
        headerName: t("operacoes.preditivos.table.total"),
        field: "total",
        suppressColumnsToolPanel: true,
        width: 100,
        cellRenderer: (params: any) => formatCurrency(params.data?.total),
      },
      {
        headerName: t("operacoes.preditivos.table.status"),
        field: "order_status",
        suppressColumnsToolPanel: true,
        width: 200,
        cellRenderer: (params: any) => {
          return params.data?.order_status ? (
            <TagCapsules
              mr={12}
              border
              colorTag={
                colorTextPredictiveOrderStatus(params.data?.order_status).color
              }
            >
              {t(
                colorTextPredictiveOrderStatus(params.data?.order_status).text
              )}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.preditivos.table.data_execution"),
        field: "sending_time",
        suppressColumnsToolPanel: true,
        width: 170,
        cellRenderer: (params: any) =>
          convertToDateAndHour(params.data?.sending_time),
      },
      {
        headerName: t("operacoes.preditivos.table.decision"),
        field: "decision",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => {
          const percentageColorText = colorTextPredictiveOrderPercentage(
            params.data?.decision
          );
          return params.data?.decision === null ||
            params.data?.decision === undefined ? (
            "----"
          ) : (
            <TagCapsules border colorTag={percentageColorText.color}>
              {`${t(percentageColorText.text)} ${
                !!percentageColorText.percentage
                  ? ` ${percentageColorText.percentage}%`
                  : ""
              }`}
            </TagCapsules>
          );
        },
      },
      {
        headerName: t("operacoes.preditivos.table.validity"),
        field: "validity",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => t(validityText(params.data?.validity)),
      },
      {
        headerName: t("operacoes.preditivos.table.order_type"),
        field: "order_type",
        suppressColumnsToolPanel: true,
        width: 180,
        cellRenderer: (params: any) =>
          t(orderTypeText(params.data?.order_type)),
      },
    ],
    [t]
  );

  return (
    <>
      <ModalTable
        open={!!id}
        setClose={setClose}
        title={t("operacoes.preditivos.modal_details.title")}
        full={true}
      >
        <AGGridDefault
          title={t("operacoes.preditivos.modal_details.table_title")}
          rowData={data?.data || []}
          columnDefs={columnDefs}
          isLoading={isLoading || isFetching}
          pagination={
            <Pagination
              pagination={pagination}
              onChange={(page) => {
                setQueryDetail((prev) => ({ ...prev, page: page }));
              }}
            />
          }
          storageKey={`columns-predictive-executrion-modal`}
          context={{
            query: queryDetail,
            setQuery: setQueryDetail,
            hasFilters: false,
          }}
        />
      </ModalTable>
    </>
  );
}
